// Inline SVG "child drawings" used as placeholder pages on the sign-in
// hero. Ported verbatim from the Claude Design handoff (drawings.jsx) so
// the sample stack matches the prototype.

const wobblyStroke = {
  stroke: "#1a1a1a",
  strokeWidth: 5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  fill: "none",
};

const PAPER = "#FFFCF3";

// Cover page — "Captain Banana Saves the Moon" hand-lettered + banana hero.
export function DrawCover() {
  return (
    <svg viewBox="0 0 300 400" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%", display: "block", background: PAPER }}>
      {/* paper texture dots — deterministic seed so SSR/hydration is stable */}
      <g opacity="0.08">
        {DOT_POSITIONS.map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r="0.8" fill="#000" />
        ))}
      </g>
      <g fontFamily='"Caveat", cursive' fill="#1a1a1a">
        <text x="150" y="48" fontSize="36" fontWeight="700" textAnchor="middle" transform="rotate(-2 150 48)">CAPTAIN</text>
        <text x="150" y="82" fontSize="36" fontWeight="700" textAnchor="middle" transform="rotate(1 150 82)">BANANA</text>
        <text x="150" y="116" fontSize="22" textAnchor="middle">saves the moon</text>
      </g>
      <g {...wobblyStroke} strokeWidth={3} fill="#F5C842">
        <path d="M 40 150 L 44 160 L 54 162 L 46 169 L 49 180 L 40 174 L 31 180 L 34 169 L 26 162 L 36 160 Z" />
        <path d="M 250 180 L 253 187 L 261 188 L 255 193 L 257 201 L 250 197 L 243 201 L 245 193 L 239 188 L 247 187 Z" />
        <path d="M 270 130 L 272 135 L 277 136 L 273 139 L 274 144 L 270 141 L 266 144 L 267 139 L 263 136 L 268 135 Z" />
      </g>
      <g transform="translate(150 270)">
        <path d="M -30 -30 L -55 80 L -10 50 L 10 50 L 55 80 L 30 -30 Z" fill="#D14A3F" stroke="#1a1a1a" strokeWidth="4" strokeLinejoin="round" />
        <path d="M -30 -50 C -45 -20, -45 30, -20 60 C 0 75, 25 75, 35 55 C 5 30, 0 -10, 5 -55 Z" fill="#FFD93D" stroke="#1a1a1a" strokeWidth="5" strokeLinejoin="round" />
        <path d="M 5 -55 L 8 -68 L 14 -65" stroke="#5C3A14" strokeWidth="4" fill="none" strokeLinecap="round" />
        <circle cx="-12" cy="-15" r="3" fill="#1a1a1a" />
        <circle cx="8" cy="-12" r="3" fill="#1a1a1a" />
        <path d="M -10 0 Q -2 8 6 -1" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
        <path d="M -22 -22 L 18 -18 L 16 -10 L -20 -14 Z" fill="#1a1a1a" opacity="0.85" />
        <circle cx="-12" cy="-15" r="3" fill="#FFD93D" />
        <circle cx="-12" cy="-15" r="2" fill="#1a1a1a" />
        <circle cx="8" cy="-12" r="3" fill="#FFD93D" />
        <circle cx="8" cy="-12" r="2" fill="#1a1a1a" />
        <path d="M -32 5 L -55 -10" {...wobblyStroke} />
        <path d="M 32 5 L 60 -5" {...wobblyStroke} />
        <path d="M -10 65 L -18 90" {...wobblyStroke} />
        <path d="M 18 65 L 22 92" {...wobblyStroke} />
      </g>
      <text x="150" y="380" fontFamily='"Caveat", cursive' fontSize="18" fill="#5C4A3E" textAnchor="middle">by Theo, age 5</text>
    </svg>
  );
}

// Banana wakes up — sun, bed, "Zzz".
export function DrawPage1() {
  return (
    <svg viewBox="0 0 300 400" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%", display: "block", background: PAPER }}>
      <g transform="translate(240 60)">
        <circle r="28" fill="#F5C842" stroke="#1a1a1a" strokeWidth="4" />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
          const r1 = 32;
          const r2 = 44;
          const x1 = Math.cos((a * Math.PI) / 180) * r1;
          const y1 = Math.sin((a * Math.PI) / 180) * r1;
          const x2 = Math.cos((a * Math.PI) / 180) * r2;
          const y2 = Math.sin((a * Math.PI) / 180) * r2;
          return <line key={a} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#1a1a1a" strokeWidth="3" strokeLinecap="round" />;
        })}
      </g>
      <rect x="40" y="240" width="220" height="100" fill="#9DCEEA" stroke="#1a1a1a" strokeWidth="5" />
      <rect x="40" y="220" width="220" height="40" fill="#FFFFFF" stroke="#1a1a1a" strokeWidth="5" />
      <rect x="55" y="200" width="80" height="40" rx="6" fill="#FFB7D6" stroke="#1a1a1a" strokeWidth="4" />
      <g transform="translate(165 220)">
        <path d="M -30 -10 C -45 10, -45 50, -20 75 C 0 88, 25 85, 35 65 C 5 40, 0 5, 5 -15 Z" fill="#FFD93D" stroke="#1a1a1a" strokeWidth="5" />
        <circle cx="-12" cy="20" r="2.5" fill="#1a1a1a" />
        <circle cx="6" cy="22" r="2.5" fill="#1a1a1a" />
        <path d="M -8 38 Q 0 44 8 36" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
      </g>
      <text x="200" y="180" fontFamily='"Caveat", cursive' fontSize="32" fill="#1a1a1a" transform="rotate(-15 200 180)">Zzz</text>
      <text x="150" y="380" fontFamily='"Caveat", cursive' fontSize="20" fill="#1a1a1a" textAnchor="middle">A bright morning.</text>
    </svg>
  );
}

// Banana puts on cape — mirror + reflection.
export function DrawPage2() {
  return (
    <svg viewBox="0 0 300 400" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%", display: "block", background: PAPER }}>
      <rect x="190" y="80" width="80" height="200" rx="10" fill="#C8E5F2" stroke="#1a1a1a" strokeWidth="4" />
      <rect x="195" y="85" width="70" height="180" fill="#E8F2F8" />
      <g transform="translate(110 200)">
        <path d="M -28 -30 L -50 70 L -8 45 L 8 45 L 50 70 L 28 -30 Z" fill="#D14A3F" stroke="#1a1a1a" strokeWidth="4" />
        <path d="M -28 -50 C -42 -20, -42 30, -18 58 C 0 70, 22 70, 32 52 C 4 28, 0 -8, 4 -52 Z" fill="#FFD93D" stroke="#1a1a1a" strokeWidth="5" />
        <circle cx="-10" cy="-15" r="3" fill="#1a1a1a" />
        <circle cx="6" cy="-13" r="3" fill="#1a1a1a" />
        <path d="M -8 0 Q 0 8 8 -1" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
        <path d="M -10 65 L -16 92" {...wobblyStroke} />
        <path d="M 14 62 L 18 92" {...wobblyStroke} />
      </g>
      <g transform="translate(232 175) scale(0.5)">
        <path d="M -28 -30 L -50 70 L -8 45 L 8 45 L 50 70 L 28 -30 Z" fill="#D14A3F" opacity="0.7" stroke="#1a1a1a" strokeWidth="4" />
        <path d="M -28 -50 C -42 -20, -42 30, -18 58 C 0 70, 22 70, 32 52 C 4 28, 0 -8, 4 -52 Z" fill="#FFD93D" stroke="#1a1a1a" strokeWidth="5" />
      </g>
      <text x="150" y="380" fontFamily='"Caveat", cursive' fontSize="20" fill="#1a1a1a" textAnchor="middle">Time to be a hero!</text>
    </svg>
  );
}

// 30 deterministic dot positions — picked once with Math.random and frozen
// so React's StrictMode double-render doesn't shimmer the texture.
const DOT_POSITIONS: [number, number][] = [
  [12, 18], [44, 33], [78, 9], [125, 27], [160, 12], [201, 41], [238, 18], [276, 35],
  [22, 72], [60, 58], [104, 88], [148, 67], [187, 95], [222, 78], [261, 110], [288, 62],
  [16, 142], [52, 168], [98, 152], [142, 188], [178, 162], [216, 198], [254, 172], [283, 206],
  [28, 248], [72, 282], [116, 261], [188, 308], [232, 288], [275, 322],
];
